import React, {
  useEffect,
  useMemo,
  useState,
} from "react";

import {
  currentChildrensBookCover,
  externalChildrensBookAssetDraft,
} from "../utils/childrensBookAssetState";

function safeExternalImageUrl(value) {
  const clean =
    String(value ?? "").trim();

  if (!clean) return "";

  try {
    const parsed =
      new URL(clean);

    if (
      parsed.protocol !== "http:" &&
      parsed.protocol !== "https:"
    ) {
      return "";
    }

    return parsed.toString();
  } catch {
    return "";
  }
}

function Field({
  label,
  value,
  onChange,
  placeholder = "",
  multiline = false,
}) {
  const sharedStyle = {
    width: "100%",
    boxSizing: "border-box",
    padding: "10px 12px",
    borderRadius: 10,
    border: "1px solid #cbd5e1",
    font: "inherit",
  };

  return (
    <label
      style={{
        display: "grid",
        gap: 7,
        fontWeight: 800,
        color: "#334155",
      }}
    >
      <span>{label}</span>

      {multiline ? (
        <textarea
          value={value}
          placeholder={placeholder}
          rows={4}
          onChange={(event) =>
            onChange(event.target.value)
          }
          style={{
            ...sharedStyle,
            resize: "vertical",
          }}
        />
      ) : (
        <input
          type="text"
          value={value}
          placeholder={placeholder}
          onChange={(event) =>
            onChange(event.target.value)
          }
          style={{
            ...sharedStyle,
            minHeight: 44,
          }}
        />
      )}
    </label>
  );
}

function SelectField({
  label,
  value,
  onChange,
  options,
}) {
  return (
    <label
      style={{
        display: "grid",
        gap: 7,
        fontWeight: 800,
        color: "#334155",
      }}
    >
      <span>{label}</span>

      <select
        value={value}
        onChange={(event) =>
          onChange(event.target.value)
        }
        style={{
          width: "100%",
          minHeight: 44,
          padding: "10px 12px",
          borderRadius: 10,
          border: "1px solid #cbd5e1",
          background: "#ffffff",
          font: "inherit",
        }}
      >
        {options.map((option) => (
          <option
            key={option.value}
            value={option.value}
          >
            {option.label}
          </option>
        ))}
      </select>
    </label>
  );
}


const BACK_COVER_FONT_OPTIONS = [
  {
    value: "",
    en: "Automatic — let NextScenes decide",
    fr: "Automatique — laisser NextScenes décider",
  },
  {
    value: "Georgia",
    en: "Georgia",
    fr: "Georgia",
  },
  {
    value: "Times New Roman",
    en: "Times New Roman",
    fr: "Times New Roman",
  },
  {
    value: "Arial",
    en: "Arial",
    fr: "Arial",
  },
  {
    value: "Verdana",
    en: "Verdana",
    fr: "Verdana",
  },
  {
    value: "Courier New",
    en: "Courier New",
    fr: "Courier New",
  },
];

function BackCoverTypographyControls({
  isFR,
  heading,
  prefix,
  coverPlan,
  update,
  allowJustify = false,
  minimumSize = 8,
  maximumSize = 36,
}) {
  const fontFamilyKey =
    `${prefix}FontFamily`;

  const fontSizeKey =
    `${prefix}FontSize`;

  const colourKey =
    `${prefix}Colour`;

  const alignmentKey =
    `${prefix}Alignment`;

  const lineSpacingKey =
    `${prefix}LineSpacing`;

  const colour =
    String(
      coverPlan[colourKey] || ""
    ).trim();

  const validColour =
    /^#[0-9a-f]{6}$/i.test(
      colour
    );

  const alignmentOptions = [
    {
      value: "auto",
      label:
        isFR
          ? "Automatique"
          : "Automatic",
    },
    {
      value: "left",
      label:
        isFR
          ? "Gauche"
          : "Left",
    },
    {
      value: "center",
      label:
        isFR
          ? "Centré"
          : "Centre",
    },
    {
      value: "right",
      label:
        isFR
          ? "Droite"
          : "Right",
    },

    ...(
      allowJustify
        ? [
            {
              value: "justify",
              label:
                isFR
                  ? "Justifié"
                  : "Justified",
            },
          ]
        : []
    ),
  ];

  return (
    <div
      style={{
        border:
          "1px solid #e2e8f0",
        borderRadius: 10,
        padding: 14,
        display: "grid",
        gap: 12,
        background: "#f8fafc",
      }}
    >
      <strong>
        {heading}
      </strong>

      <SelectField
        label={
          isFR
            ? "Police"
            : "Font"
        }
        value={
          coverPlan[
            fontFamilyKey
          ] ||
          ""
        }
        onChange={(value) =>
          update(
            fontFamilyKey,
            value
          )
        }
        options={
          BACK_COVER_FONT_OPTIONS.map(
            (option) => ({
              value:
                option.value,

              label:
                isFR
                  ? option.fr
                  : option.en,
            })
          )
        }
      />

      <label
        style={{
          display: "grid",
          gap: 7,
          fontWeight: 800,
          color: "#334155",
        }}
      >
        <span>
          {isFR
            ? "Taille de police"
            : "Font size"}
        </span>

        <input
          type="number"
          min={minimumSize}
          max={maximumSize}
          step="0.5"
          value={
            coverPlan[
              fontSizeKey
            ] ||
            ""
          }
          placeholder={
            isFR
              ? "Automatique"
              : "Automatic"
          }
          onChange={(event) =>
            update(
              fontSizeKey,
              event.target.value
            )
          }
          style={{
            width: "100%",
            minHeight: 44,
            boxSizing:
              "border-box",
            padding:
              "10px 12px",
            borderRadius: 10,
            border:
              "1px solid #cbd5e1",
            font: "inherit",
          }}
        />

        <span
          className="ns-card-meta"
        >
          {isFR
            ? "Laissez vide pour la taille automatique."
            : "Leave blank for automatic sizing."}
        </span>
      </label>

      <label
        style={{
          display: "grid",
          gap: 7,
          fontWeight: 800,
          color: "#334155",
        }}
      >
        <span>
          {isFR
            ? "Couleur du texte"
            : "Text colour"}
        </span>

        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 10,
            flexWrap: "wrap",
          }}
        >
          <input
            type="color"
            value={
              validColour
                ? colour
                : "#ffffff"
            }
            onChange={(event) =>
              update(
                colourKey,
                event.target.value
              )
            }
            aria-label={
              isFR
                ? "Choisir la couleur du texte"
                : "Choose text colour"
            }
            style={{
              width: 54,
              height: 40,
              padding: 2,
            }}
          />

          <button
            type="button"
            className="ns-button"
            onClick={() =>
              update(
                colourKey,
                ""
              )
            }
          >
            {validColour
              ? (
                  isFR
                    ? "Utiliser la couleur automatique"
                    : "Use automatic colour"
                )
              : (
                  isFR
                    ? "Couleur automatique"
                    : "Automatic colour"
                )}
          </button>

          {validColour ? (
            <code>
              {colour.toUpperCase()}
            </code>
          ) : null}
        </div>
      </label>

      <SelectField
        label={
          isFR
            ? "Alignement"
            : "Alignment"
        }
        value={
          coverPlan[
            alignmentKey
          ] ||
          "auto"
        }
        onChange={(value) =>
          update(
            alignmentKey,
            value
          )
        }
        options={
          alignmentOptions
        }
      />

      <SelectField
        label={
          isFR
            ? "Interligne"
            : "Line spacing"
        }
        value={
          coverPlan[
            lineSpacingKey
          ] ||
          ""
        }
        onChange={(value) =>
          update(
            lineSpacingKey,
            value
          )
        }
        options={[
          {
            value: "",
            label:
              isFR
                ? "Automatique"
                : "Automatic",
          },
          {
            value: "1",
            label: "1.0",
          },
          {
            value: "1.15",
            label: "1.15",
          },
          {
            value: "1.25",
            label: "1.25",
          },
          {
            value: "1.35",
            label: "1.35",
          },
          {
            value: "1.5",
            label: "1.5",
          },
        ]}
      />

      {allowJustify ? (
        <span
          className="ns-card-meta"
          style={{
            lineHeight: 1.45,
          }}
        >
          {isFR
            ? "L’alignement justifié sera appliqué par la mise en page de production, avec la dernière ligne de chaque paragraphe laissée naturelle."
            : "Justified alignment will be applied by production layout, with the final line of each paragraph left natural."}
        </span>
      ) : null}
    </div>
  );
}

export default function ChildrensBookCoverPlanning({
  lang = "en",
  coverPlan,
  projectPlan,
  assetManagementEnabled = false,
  childrensBookAssets = [],
  childrensBookAssetsLoading = false,
  childrensBookAssetsError = "",
  childrensBookAssetActionId = "",
  onLoadChildrensBookAssetFile,
  onUploadChildrensBookCover,
  onRegisterChildrensBookAsset,
  onReplaceChildrensBookAsset,
  onRemoveChildrensBookAsset,
  onChange,
  returnToWholeBookReview = false,
  onReturn,
  onExit,
}) {
  const isFR =
    String(lang).toLowerCase() === "fr";

  const [reviewingCover, setReviewingCover] =
    useState(false);

  const [
    coverSourceMode,
    setCoverSourceMode,
  ] = useState("file");

  const [
    selectedCoverFile,
    setSelectedCoverFile,
  ] = useState(null);

  const [
    selectedCoverPreviewUrl,
    setSelectedCoverPreviewUrl,
  ] = useState("");

  const [
    persistedCoverPreviewUrl,
    setPersistedCoverPreviewUrl,
  ] = useState("");

  const [
    persistedCoverPreviewLoading,
    setPersistedCoverPreviewLoading,
  ] = useState(false);

  const [
    persistedCoverPreviewError,
    setPersistedCoverPreviewError,
  ] = useState("");

  const [
    existingCoverUrl,
    setExistingCoverUrl,
  ] = useState("");

  const [
    existingCoverRightsConfirmed,
    setExistingCoverRightsConfirmed,
  ] = useState(false);

  const [
    existingCoverSubmitting,
    setExistingCoverSubmitting,
  ] = useState(false);

  const [
    existingCoverMessage,
    setExistingCoverMessage,
  ] = useState("");

  const [
    existingCoverError,
    setExistingCoverError,
  ] = useState("");

  const [
    pendingCoverRemoval,
    setPendingCoverRemoval,
  ] = useState(false);

  const [
    selectedBackCoverFile,
    setSelectedBackCoverFile,
  ] = useState(null);

  const [
    backCoverRightsConfirmed,
    setBackCoverRightsConfirmed,
  ] = useState(false);

  const [
    backCoverSubmitting,
    setBackCoverSubmitting,
  ] = useState(false);

  const [
    backCoverMessage,
    setBackCoverMessage,
  ] = useState("");

  const [
    backCoverError,
    setBackCoverError,
  ] = useState("");

  const [
    persistedBackCoverPreviewUrl,
    setPersistedBackCoverPreviewUrl,
  ] = useState("");

  const [
    persistedBackCoverPreviewLoading,
    setPersistedBackCoverPreviewLoading,
  ] = useState(false);

  const [
    persistedBackCoverPreviewError,
    setPersistedBackCoverPreviewError,
  ] = useState("");

  const [
    selectedAuthorPhotoFile,
    setSelectedAuthorPhotoFile,
  ] = useState(null);

  const [
    authorPhotoRightsConfirmed,
    setAuthorPhotoRightsConfirmed,
  ] = useState(false);

  const [
    authorPhotoSubmitting,
    setAuthorPhotoSubmitting,
  ] = useState(false);

  const [
    authorPhotoMessage,
    setAuthorPhotoMessage,
  ] = useState("");

  const [
    authorPhotoError,
    setAuthorPhotoError,
  ] = useState("");

  const [
    persistedAuthorPhotoPreviewUrl,
    setPersistedAuthorPhotoPreviewUrl,
  ] = useState("");

  const [
    persistedAuthorPhotoPreviewLoading,
    setPersistedAuthorPhotoPreviewLoading,
  ] = useState(false);

  const [
    persistedAuthorPhotoPreviewError,
    setPersistedAuthorPhotoPreviewError,
  ] = useState("");

  const [
    pendingAuthorPhotoRemoval,
    setPendingAuthorPhotoRemoval,
  ] = useState(false);

  const activeFrontCover =
    useMemo(
      () =>
        currentChildrensBookCover(
          childrensBookAssets,
          "front_cover"
        ),
      [childrensBookAssets]
    );

  const activeBackCover =
    useMemo(
      () =>
        currentChildrensBookCover(
          childrensBookAssets,
          "back_cover"
        ),
      [childrensBookAssets]
    );

  const activeAuthorPhoto =
    useMemo(
      () =>
        currentChildrensBookCover(
          childrensBookAssets,
          "author_photo"
        ),
      [childrensBookAssets]
    );

  const activeFrontCoverNeedsAuthentication =
    Boolean(
      activeFrontCover &&
      (
        activeFrontCover.source ===
          "upload" ||
        activeFrontCover.storageProvider ===
          "local"
      )
    );

  const activeBackCoverNeedsAuthentication =
    Boolean(
      activeBackCover &&
      (
        activeBackCover.source ===
          "upload" ||
        activeBackCover.storageProvider ===
          "local"
      )
    );

  const activeAuthorPhotoNeedsAuthentication =
    Boolean(
      activeAuthorPhoto &&
      (
        activeAuthorPhoto.source ===
          "upload" ||
        activeAuthorPhoto.storageProvider ===
          "local"
      )
    );

  useEffect(() => {
    let cancelled =
      false;

    let objectUrl =
      "";

    const publicUrl =
      String(
        activeFrontCover?.publicUrl ||
        ""
      ).trim();

    setPersistedCoverPreviewUrl("");
    setPersistedCoverPreviewError("");

    if (
      !activeFrontCoverNeedsAuthentication ||
      !publicUrl ||
      typeof onLoadChildrensBookAssetFile !==
        "function"
    ) {
      setPersistedCoverPreviewLoading(
        false
      );

      return () => {};
    }

    setPersistedCoverPreviewLoading(
      true
    );

    async function loadPreview() {
      try {
        const blob =
          await onLoadChildrensBookAssetFile(
            publicUrl
          );

        if (cancelled) {
          return;
        }

        if (
          typeof URL === "undefined" ||
          typeof URL.createObjectURL !==
            "function"
        ) {
          throw new Error(
            "The browser could not prepare the cover preview."
          );
        }

        objectUrl =
          URL.createObjectURL(
            blob
          );

        if (cancelled) {
          URL.revokeObjectURL(
            objectUrl
          );

          objectUrl = "";
          return;
        }

        setPersistedCoverPreviewUrl(
          objectUrl
        );
      } catch (error) {
        if (!cancelled) {
          setPersistedCoverPreviewError(
            String(
              error?.message ||
              (
                isFR
                  ? "Impossible de charger l’aperçu de la couverture enregistrée."
                  : "Could not load the saved cover preview."
              )
            ).trim()
          );
        }
      } finally {
        if (!cancelled) {
          setPersistedCoverPreviewLoading(
            false
          );
        }
      }
    }

    loadPreview();

    return () => {
      cancelled = true;

      if (
        objectUrl &&
        typeof URL !== "undefined" &&
        typeof URL.revokeObjectURL ===
          "function"
      ) {
        URL.revokeObjectURL(
          objectUrl
        );
      }
    };
  }, [
    activeFrontCover?.publicUrl,
    activeFrontCoverNeedsAuthentication,
    isFR,
    onLoadChildrensBookAssetFile,
  ]);

  useEffect(() => {
    let cancelled =
      false;

    let objectUrl =
      "";

    const publicUrl =
      String(
        activeBackCover?.publicUrl ||
        ""
      ).trim();

    setPersistedBackCoverPreviewUrl("");
    setPersistedBackCoverPreviewError("");

    if (
      !activeBackCoverNeedsAuthentication ||
      !publicUrl ||
      typeof onLoadChildrensBookAssetFile !==
        "function"
    ) {
      setPersistedBackCoverPreviewLoading(
        false
      );

      return () => {};
    }

    setPersistedBackCoverPreviewLoading(
      true
    );

    async function loadBackCoverPreview() {
      try {
        const blob =
          await onLoadChildrensBookAssetFile(
            publicUrl
          );

        if (cancelled) {
          return;
        }

        if (
          typeof URL === "undefined" ||
          typeof URL.createObjectURL !==
            "function"
        ) {
          throw new Error(
            "The browser could not prepare the back-cover preview."
          );
        }

        objectUrl =
          URL.createObjectURL(
            blob
          );

        if (cancelled) {
          URL.revokeObjectURL(
            objectUrl
          );

          objectUrl = "";
          return;
        }

        setPersistedBackCoverPreviewUrl(
          objectUrl
        );
      } catch (error) {
        if (!cancelled) {
          setPersistedBackCoverPreviewError(
            String(
              error?.message ||
              (
                isFR
                  ? "Impossible de charger l’aperçu de l’image de quatrième de couverture enregistrée."
                  : "Could not load the saved back-cover image preview."
              )
            ).trim()
          );
        }
      } finally {
        if (!cancelled) {
          setPersistedBackCoverPreviewLoading(
            false
          );
        }
      }
    }

    loadBackCoverPreview();

    return () => {
      cancelled = true;

      if (
        objectUrl &&
        typeof URL !== "undefined" &&
        typeof URL.revokeObjectURL ===
          "function"
      ) {
        URL.revokeObjectURL(
          objectUrl
        );
      }
    };
  }, [
    activeBackCover?.publicUrl,
    activeBackCoverNeedsAuthentication,
    isFR,
    onLoadChildrensBookAssetFile,
  ]);

  useEffect(() => {
    let cancelled =
      false;

    let objectUrl =
      "";

    const publicUrl =
      String(
        activeAuthorPhoto?.publicUrl ||
        ""
      ).trim();

    setPersistedAuthorPhotoPreviewUrl("");
    setPersistedAuthorPhotoPreviewError("");

    if (
      !activeAuthorPhotoNeedsAuthentication ||
      !publicUrl ||
      typeof onLoadChildrensBookAssetFile !==
        "function"
    ) {
      setPersistedAuthorPhotoPreviewLoading(
        false
      );

      return () => {};
    }

    setPersistedAuthorPhotoPreviewLoading(
      true
    );

    async function loadAuthorPhotoPreview() {
      try {
        const blob =
          await onLoadChildrensBookAssetFile(
            publicUrl
          );

        if (cancelled) {
          return;
        }

        if (
          typeof URL === "undefined" ||
          typeof URL.createObjectURL !==
            "function"
        ) {
          throw new Error(
            "The browser could not prepare the author-photo preview."
          );
        }

        objectUrl =
          URL.createObjectURL(
            blob
          );

        if (cancelled) {
          URL.revokeObjectURL(
            objectUrl
          );

          objectUrl = "";
          return;
        }

        setPersistedAuthorPhotoPreviewUrl(
          objectUrl
        );
      } catch (error) {
        if (!cancelled) {
          setPersistedAuthorPhotoPreviewError(
            String(
              error?.message ||
              (
                isFR
                  ? "Impossible de charger l’aperçu de la photo d’auteur enregistrée."
                  : "Could not load the saved author-photo preview."
              )
            ).trim()
          );
        }
      } finally {
        if (!cancelled) {
          setPersistedAuthorPhotoPreviewLoading(
            false
          );
        }
      }
    }

    loadAuthorPhotoPreview();

    return () => {
      cancelled = true;

      if (
        objectUrl &&
        typeof URL !== "undefined" &&
        typeof URL.revokeObjectURL ===
          "function"
      ) {
        URL.revokeObjectURL(
          objectUrl
        );
      }
    };
  }, [
    activeAuthorPhoto?.publicUrl,
    activeAuthorPhotoNeedsAuthentication,
    isFR,
    onLoadChildrensBookAssetFile,
  ]);

  const authorPhotoPreviewUrl =
    persistedAuthorPhotoPreviewUrl ||
    (
      activeAuthorPhotoNeedsAuthentication
        ? ""
        : safeExternalImageUrl(
            activeAuthorPhoto?.publicUrl
          )
    );

  const backCoverPreviewUrl =
    persistedBackCoverPreviewUrl ||
    (
      activeBackCoverNeedsAuthentication
        ? ""
        : safeExternalImageUrl(
            activeBackCover?.publicUrl
          )
    );

  const previewCoverUrl =
    selectedCoverPreviewUrl ||
    persistedCoverPreviewUrl ||
    (
      coverSourceMode === "url"
        ? safeExternalImageUrl(
            existingCoverUrl
          )
        : ""
    ) ||
    (
      activeFrontCoverNeedsAuthentication
        ? ""
        : safeExternalImageUrl(
            activeFrontCover?.publicUrl
          )
    );

  const assetBusy =
    existingCoverSubmitting ||
    Boolean(
      String(
        childrensBookAssetActionId || ""
      ).trim()
    );

  useEffect(() => {
    return () => {
      if (
        selectedCoverPreviewUrl &&
        typeof URL !== "undefined" &&
        typeof URL.revokeObjectURL ===
          "function"
      ) {
        URL.revokeObjectURL(
          selectedCoverPreviewUrl
        );
      }
    };
  }, [selectedCoverPreviewUrl]);

  const fieldGrid = {
    display: "grid",
    gridTemplateColumns:
      "repeat(auto-fit, minmax(240px, 1fr))",
    gap: 16,
  };

  function update(field, value) {
    onChange(field, value);
  }

  function selectExistingCoverFile(
    event
  ) {
    const file =
      event.target.files?.[0] ||
      null;

    setExistingCoverMessage("");
    setExistingCoverError("");
    setSelectedCoverFile(null);
    setSelectedCoverPreviewUrl("");

    if (!file) {
      return;
    }

    const allowedTypes =
      new Set([
        "image/jpeg",
        "image/png",
        "image/webp",
      ]);

    const mimeType =
      String(file.type || "")
        .trim()
        .toLowerCase();

    if (!allowedTypes.has(mimeType)) {
      event.target.value = "";

      setExistingCoverError(
        isFR
          ? "Choisissez une image JPG, PNG ou WEBP."
          : "Choose a JPG, PNG, or WEBP image."
      );

      return;
    }

    if (
      Number(file.size || 0) >
      5 * 1024 * 1024
    ) {
      event.target.value = "";

      setExistingCoverError(
        isFR
          ? "L’image de couverture ne doit pas dépasser 5 Mo."
          : "The cover image must not exceed 5 MB."
      );

      return;
    }

    setSelectedCoverFile(file);

    if (
      typeof URL !== "undefined" &&
      typeof URL.createObjectURL ===
        "function"
    ) {
      setSelectedCoverPreviewUrl(
        URL.createObjectURL(file)
      );
    }
  }

  async function uploadSelectedCover() {
    setExistingCoverMessage("");
    setExistingCoverError("");

    if (!assetManagementEnabled) {
      setExistingCoverError(
        isFR
          ? "Enregistrez d’abord le projet avant de téléverser une couverture."
          : "Save the project before uploading a cover."
      );

      return;
    }

    if (!selectedCoverFile) {
      setExistingCoverError(
        isFR
          ? "Choisissez d’abord une image de couverture."
          : "Choose a cover image first."
      );

      return;
    }

    if (!existingCoverRightsConfirmed) {
      setExistingCoverError(
        isFR
          ? "Confirmez que vous détenez les droits ou l’autorisation d’utiliser cette couverture."
          : "Confirm that you own or have permission to use this cover."
      );

      return;
    }

    if (
      typeof onUploadChildrensBookCover !==
      "function"
    ) {
      setExistingCoverError(
        isFR
          ? "Le téléversement de couverture n’est pas disponible."
          : "Cover upload is unavailable."
      );

      return;
    }

    try {
      setExistingCoverSubmitting(true);

      await onUploadChildrensBookCover(
        selectedCoverFile
      );

      setExistingCoverMessage(
        activeFrontCover
          ? (
              isFR
                ? "La couverture a été remplacée."
                : "The cover has been replaced."
            )
          : (
              isFR
                ? "La couverture a été ajoutée au projet."
                : "The cover has been added to the project."
            )
      );

      setSelectedCoverFile(null);
      setSelectedCoverPreviewUrl("");
      setExistingCoverRightsConfirmed(
        false
      );
    } catch (error) {
      setExistingCoverError(
        String(
          error?.message ||
          (
            isFR
              ? "Impossible de téléverser cette couverture."
              : "Could not upload this cover."
          )
        ).trim()
      );
    } finally {
      setExistingCoverSubmitting(false);
    }
  }

  async function saveExistingCover() {
    const publicUrl =
      safeExternalImageUrl(
        existingCoverUrl
      );

    setExistingCoverMessage("");
    setExistingCoverError("");

    if (!assetManagementEnabled) {
      setExistingCoverError(
        isFR
          ? "Enregistrez d’abord le projet avant d’ajouter une couverture."
          : "Save the project before adding a cover."
      );

      return;
    }

    if (!publicUrl) {
      setExistingCoverError(
        isFR
          ? "Saisissez une adresse d’image HTTP ou HTTPS valide."
          : "Enter a valid HTTP or HTTPS image address."
      );

      return;
    }

    if (!existingCoverRightsConfirmed) {
      setExistingCoverError(
        isFR
          ? "Confirmez que vous détenez les droits ou l’autorisation d’utiliser cette couverture."
          : "Confirm that you own or have permission to use this cover."
      );

      return;
    }

    const asset =
      externalChildrensBookAssetDraft({
        assetType: "cover",
        purpose: "front_cover",
        publicUrl,
      });

    try {
      setExistingCoverSubmitting(true);

      if (activeFrontCover) {
        if (
          typeof onReplaceChildrensBookAsset !==
          "function"
        ) {
          throw new Error(
            isFR
              ? "Le remplacement de couverture n’est pas disponible."
              : "Cover replacement is unavailable."
          );
        }

        await onReplaceChildrensBookAsset(
          activeFrontCover.id,
          activeFrontCover.revision,
          asset
        );

        setExistingCoverMessage(
          isFR
            ? "La couverture existante a été remplacée."
            : "The existing cover has been replaced."
        );
      } else {
        if (
          typeof onRegisterChildrensBookAsset !==
          "function"
        ) {
          throw new Error(
            isFR
              ? "L’enregistrement de couverture n’est pas disponible."
              : "Cover registration is unavailable."
          );
        }

        await onRegisterChildrensBookAsset(
          asset
        );

        setExistingCoverMessage(
          isFR
            ? "La couverture existante a été ajoutée au projet."
            : "The existing cover has been added to the project."
        );
      }

      setExistingCoverUrl("");
      setSelectedCoverFile(null);
      setSelectedCoverPreviewUrl("");
      setPersistedCoverPreviewUrl("");
      setPersistedCoverPreviewError("");

      setExistingCoverRightsConfirmed(
        false
      );
    } catch (error) {
      setExistingCoverError(
        String(
          error?.message ||
          (
            isFR
              ? "Impossible d’enregistrer cette couverture."
              : "Could not save this cover."
          )
        ).trim()
      );
    } finally {
      setExistingCoverSubmitting(false);
    }
  }

  function requestExistingCoverRemoval() {
    if (!activeFrontCover) {
      return;
    }

    setPendingCoverRemoval(true);
  }

  function cancelExistingCoverRemoval() {
    setPendingCoverRemoval(false);
  }

  async function confirmExistingCoverRemoval(
    event
  ) {
    event.preventDefault();

    if (
      !activeFrontCover ||
      typeof onRemoveChildrensBookAsset !==
        "function"
    ) {
      setPendingCoverRemoval(false);
      return;
    }

    try {
      setExistingCoverSubmitting(true);
      setExistingCoverMessage("");
      setExistingCoverError("");

      await onRemoveChildrensBookAsset(
        activeFrontCover.id,
        activeFrontCover.revision
      );

      setExistingCoverMessage(
        isFR
          ? "La couverture a été retirée du projet sans supprimer le fichier source."
          : "The cover was removed from the project without deleting the source file."
      );

      setExistingCoverUrl("");
      setSelectedCoverFile(null);
      setSelectedCoverPreviewUrl("");
      setPersistedCoverPreviewUrl("");
      setPersistedCoverPreviewError("");

      setExistingCoverRightsConfirmed(
        false
      );

      setPendingCoverRemoval(false);
    } catch (error) {
      setExistingCoverError(
        String(
          error?.message ||
          (
            isFR
              ? "Impossible de retirer cette couverture."
              : "Could not remove this cover."
          )
        ).trim()
      );
    } finally {
      setExistingCoverSubmitting(false);
    }
  }

  function selectBackCoverFile(event) {
    const file =
      event.target.files?.[0] ||
      null;

    setBackCoverMessage("");
    setBackCoverError("");
    setSelectedBackCoverFile(null);

    if (!file) {
      return;
    }

    const mimeType =
      String(file.type || "")
        .trim()
        .toLowerCase();

    if (
      ![
        "image/jpeg",
        "image/png",
        "image/webp",
      ].includes(mimeType)
    ) {
      event.target.value = "";

      setBackCoverError(
        isFR
          ? "Choisissez une image JPG, PNG ou WEBP."
          : "Choose a JPG, PNG, or WEBP image."
      );

      return;
    }

    if (
      Number(file.size || 0) >
      5 * 1024 * 1024
    ) {
      event.target.value = "";

      setBackCoverError(
        isFR
          ? "L’image de quatrième de couverture ne doit pas dépasser 5 Mo."
          : "The back-cover image must not exceed 5 MB."
      );

      return;
    }

    setSelectedBackCoverFile(
      file
    );
  }

  async function uploadSelectedBackCover() {
    setBackCoverMessage("");
    setBackCoverError("");

    if (!selectedBackCoverFile) {
      setBackCoverError(
        isFR
          ? "Choisissez d’abord une image de quatrième de couverture."
          : "Choose a back-cover image first."
      );
      return;
    }

    if (!backCoverRightsConfirmed) {
      setBackCoverError(
        isFR
          ? "Confirmez que vous détenez les droits ou l’autorisation d’utiliser cette image."
          : "Confirm that you own or have permission to use this image."
      );
      return;
    }

    if (
      typeof onUploadChildrensBookCover !==
        "function"
    ) {
      setBackCoverError(
        isFR
          ? "Le téléversement de l’image de quatrième de couverture n’est pas disponible."
          : "Back-cover image upload is unavailable."
      );
      return;
    }

    try {
      setBackCoverSubmitting(true);

      await onUploadChildrensBookCover(
        selectedBackCoverFile,
        "back_cover"
      );

      setBackCoverMessage(
        activeBackCover
          ? (
              isFR
                ? "L’image de quatrième de couverture a été remplacée."
                : "The back-cover image has been replaced."
            )
          : (
              isFR
                ? "L’image de quatrième de couverture a été ajoutée au projet."
                : "The back-cover image has been added to the project."
            )
      );

      setSelectedBackCoverFile(null);
      setBackCoverRightsConfirmed(false);
    } catch (error) {
      setBackCoverError(
        String(
          error?.message ||
          (
            isFR
              ? "Impossible de téléverser cette image de quatrième de couverture."
              : "Could not upload this back-cover image."
          )
        ).trim()
      );
    } finally {
      setBackCoverSubmitting(false);
    }
  }

  function selectAuthorPhotoFile(event) {
    const file =
      event.target.files?.[0] ||
      null;

    setAuthorPhotoMessage("");
    setAuthorPhotoError("");
    setSelectedAuthorPhotoFile(null);

    if (!file) {
      return;
    }

    const mimeType =
      String(file.type || "")
        .trim()
        .toLowerCase();

    if (
      ![
        "image/jpeg",
        "image/png",
        "image/webp",
      ].includes(mimeType)
    ) {
      event.target.value = "";

      setAuthorPhotoError(
        isFR
          ? "Choisissez une photo JPG, PNG ou WEBP."
          : "Choose a JPG, PNG, or WEBP author photo."
      );

      return;
    }

    if (
      Number(file.size || 0) >
      5 * 1024 * 1024
    ) {
      event.target.value = "";

      setAuthorPhotoError(
        isFR
          ? "La photo d’auteur ne doit pas dépasser 5 Mo."
          : "The author photo must not exceed 5 MB."
      );

      return;
    }

    setSelectedAuthorPhotoFile(
      file
    );
  }

  async function uploadSelectedAuthorPhoto() {
    setAuthorPhotoMessage("");
    setAuthorPhotoError("");

    if (!selectedAuthorPhotoFile) {
      setAuthorPhotoError(
        isFR
          ? "Choisissez d’abord une photo d’auteur."
          : "Choose an author photo first."
      );
      return;
    }

    if (!authorPhotoRightsConfirmed) {
      setAuthorPhotoError(
        isFR
          ? "Confirmez que vous détenez les droits ou l’autorisation d’utiliser cette photo."
          : "Confirm that you own or have permission to use this author photo."
      );
      return;
    }

    if (
      typeof onUploadChildrensBookCover !==
        "function"
    ) {
      setAuthorPhotoError(
        isFR
          ? "Le téléversement de la photo d’auteur n’est pas disponible."
          : "Author photo upload is unavailable."
      );
      return;
    }

    try {
      setAuthorPhotoSubmitting(true);

      await onUploadChildrensBookCover(
        selectedAuthorPhotoFile,
        "author_photo"
      );

      setAuthorPhotoMessage(
        activeAuthorPhoto
          ? (
              isFR
                ? "La photo d’auteur a été remplacée."
                : "The author photo has been replaced."
            )
          : (
              isFR
                ? "La photo d’auteur a été ajoutée au projet."
                : "The author photo has been added to the project."
            )
      );

      setSelectedAuthorPhotoFile(null);
      setAuthorPhotoRightsConfirmed(false);
    } catch (error) {
      setAuthorPhotoError(
        String(
          error?.message ||
          (
            isFR
              ? "Impossible de téléverser cette photo d’auteur."
              : "Could not upload this author photo."
          )
        ).trim()
      );
    } finally {
      setAuthorPhotoSubmitting(false);
    }
  }

  function requestAuthorPhotoRemoval() {
    if (!activeAuthorPhoto) {
      return;
    }

    setPendingAuthorPhotoRemoval(
      true
    );

    setAuthorPhotoMessage("");
    setAuthorPhotoError("");
  }

  function cancelAuthorPhotoRemoval() {
    setPendingAuthorPhotoRemoval(
      false
    );
  }

  async function confirmAuthorPhotoRemoval() {
    if (
      !activeAuthorPhoto ||
      typeof onRemoveChildrensBookAsset !==
        "function"
    ) {
      setPendingAuthorPhotoRemoval(
        false
      );

      return;
    }

    try {
      setAuthorPhotoSubmitting(
        true
      );

      setAuthorPhotoMessage("");
      setAuthorPhotoError("");

      await onRemoveChildrensBookAsset(
        activeAuthorPhoto.id,
        activeAuthorPhoto.revision
      );

      setPersistedAuthorPhotoPreviewUrl(
        ""
      );

      setPersistedAuthorPhotoPreviewError(
        ""
      );

      setSelectedAuthorPhotoFile(
        null
      );

      setAuthorPhotoRightsConfirmed(
        false
      );

      setPendingAuthorPhotoRemoval(
        false
      );

      setAuthorPhotoMessage(
        isFR
          ? "La photo d’auteur a été retirée du projet sans supprimer le fichier source."
          : "The author photo was removed from the project without deleting the source file."
      );
    } catch (error) {
      setAuthorPhotoError(
        String(
          error?.message ||
          (
            isFR
              ? "Impossible de retirer cette photo d’auteur."
              : "Could not remove this author photo."
          )
        ).trim()
      );
    } finally {
      setAuthorPhotoSubmitting(
        false
      );
    }
  }

  function renderExistingCoverPanel() {
    return (
      <article
        className="ns-card ns-nbb-feature-panel"
        style={{
          padding:
            "clamp(18px, 3vw, 24px)",
          marginBottom: 18,
          borderRadius: 18,
          border:
            "1px solid rgba(37,99,235,0.24)",
          background:
            "linear-gradient(145deg, #ffffff, #eff6ff)",
        }}
      >
        <div
          style={{
            display: "flex",
            justifyContent:
              "space-between",
            alignItems: "flex-start",
            gap: 14,
            flexWrap: "wrap",
          }}
        >
          <div>
            <div
              style={{
                fontSize: 12,
                fontWeight: 900,
                letterSpacing:
                  "0.1em",
                textTransform:
                  "uppercase",
                color: "#1d4ed8",
              }}
            >
              {isFR
                ? "Couverture existante autorisée"
                : "Authorised Existing Cover"}
            </div>

            <h3
              className="ns-card-title"
              style={{
                margin: "6px 0 7px",
              }}
            >
              {activeFrontCover
                ? (
                    isFR
                      ? "Couverture actuellement sélectionnée"
                      : "Current Selected Cover"
                  )
                : (
                    isFR
                      ? "Sélectionner une couverture existante"
                      : "Select an Existing Cover"
                  )}
            </h3>

            <p
              className="ns-card-meta"
              style={{
                margin: 0,
                lineHeight: 1.55,
                maxWidth: 720,
              }}
            >
              {activeFrontCover
                ? (
                    isFR
                      ? "Cette couverture est enregistrée comme ressource active du projet. Vous pouvez la remplacer ou la retirer sans supprimer le fichier source."
                      : "This cover is stored as the project’s active cover asset. You may replace or remove it without deleting the source file."
                  )
                : coverSourceMode === "file"
                ? (
                    isFR
                      ? "Choisissez une image JPG, PNG ou WEBP que vous possédez ou que vous êtes autorisé à utiliser."
                      : "Choose a JPG, PNG, or WEBP image that you own or have permission to use."
                  )
                : (
                    isFR
                      ? "Saisissez l’adresse sécurisée d’une image que vous possédez ou que vous êtes autorisé à utiliser."
                      : "Enter the secure address of an image that you own or have permission to use."
                  )}
            </p>
          </div>

          {childrensBookAssetsLoading ? (
            <span
              className="ns-card-meta"
              role="status"
            >
              {isFR
                ? "Chargement…"
                : "Loading…"}
            </span>
          ) : null}
        </div>

        {persistedCoverPreviewLoading ? (
          <div
            role="status"
            className="ns-card-meta"
            style={{
              marginTop: 16,
              fontWeight: 800,
            }}
          >
            {isFR
              ? "Chargement de la couverture enregistrée…"
              : "Loading the saved cover…"}
          </div>
        ) : null}

        {persistedCoverPreviewError ? (
          <div
            role="alert"
            style={{
              marginTop: 16,
              color: "#b91c1c",
              fontWeight: 800,
            }}
          >
            {persistedCoverPreviewError}
          </div>
        ) : null}

        {previewCoverUrl ? (
          <div
            style={{
              marginTop: 16,
              display: "grid",
              placeItems: "center",
              padding: 16,
              borderRadius: 14,
              background: "#ffffff",
              border:
                "1px solid rgba(148,163,184,0.35)",
            }}
          >
            <img
              src={previewCoverUrl}
              alt={
                isFR
                  ? "Aperçu de la couverture sélectionnée"
                  : "Preview of the selected cover"
              }
              style={{
                display: "block",
                width: "100%",
                maxWidth: 320,
                maxHeight: 440,
                objectFit: "contain",
                borderRadius: 10,
              }}
            />
          </div>
        ) : null}

        <div
          style={{
            display: "flex",
            gap: 10,
            flexWrap: "wrap",
            marginTop: 16,
          }}
        >
          <button
            className={
              coverSourceMode === "file"
                ? "ns-button ns-button-primary"
                : "ns-button ns-button-outline"
            }
            type="button"
            onClick={() => {
              setCoverSourceMode("file");
              setExistingCoverMessage("");
              setExistingCoverError("");
            }}
          >
            {isFR
              ? "Choisir une couverture existante"
              : "Choose Existing Cover"}
          </button>

          <button
            className={
              coverSourceMode === "url"
                ? "ns-button ns-button-primary"
                : "ns-button ns-button-outline"
            }
            type="button"
            onClick={() => {
              setCoverSourceMode("url");
              setExistingCoverMessage("");
              setExistingCoverError("");
            }}
          >
            {isFR
              ? "Utiliser plutôt une adresse d’image"
              : "Use an Image URL Instead"}
          </button>
        </div>

        {coverSourceMode === "file" ? (
          <label
            style={{
              display: "grid",
              gap: 7,
              marginTop: 16,
              fontWeight: 800,
              color: "#334155",
            }}
          >
            <span>
              {isFR
                ? "Fichier de couverture"
                : "Cover image file"}
            </span>

            <input
              key={
                selectedCoverFile
                  ? `${
                      selectedCoverFile.name
                    }-${
                      selectedCoverFile.lastModified
                    }`
                  : "empty-cover-file"
              }
              type="file"
              accept="image/jpeg,image/png,image/webp"
              disabled={
                !assetManagementEnabled ||
                assetBusy
              }
              onChange={
                selectExistingCoverFile
              }
              style={{
                width: "100%",
                boxSizing: "border-box",
                padding: "10px 12px",
                borderRadius: 10,
                border:
                  "1px solid #cbd5e1",
                background: "#ffffff",
                font: "inherit",
              }}
            />

            <span
              className="ns-card-meta"
              style={{
                fontWeight: 500,
              }}
            >
              {isFR
                ? "JPG, PNG ou WEBP. Taille maximale : 5 Mo."
                : "JPG, PNG, or WEBP. Maximum size: 5 MB."}
            </span>

            {selectedCoverFile ? (
              <span
                style={{
                  color: "#166534",
                  fontWeight: 800,
                }}
              >
                {selectedCoverFile.name}
              </span>
            ) : null}
          </label>
        ) : (
          <label
            style={{
              display: "grid",
              gap: 7,
              marginTop: 16,
              fontWeight: 800,
              color: "#334155",
            }}
          >
            <span>
              {isFR
                ? "Adresse de l’image de couverture"
                : "Cover image address"}
            </span>

            <input
              type="url"
              value={existingCoverUrl}
              placeholder="https://example.com/cover.jpg"
              onChange={(event) => {
                setExistingCoverUrl(
                  event.target.value
                );

                setExistingCoverMessage("");
                setExistingCoverError("");
              }}
              style={{
                width: "100%",
                minHeight: 44,
                boxSizing: "border-box",
                padding: "10px 12px",
                borderRadius: 10,
                border:
                  "1px solid #cbd5e1",
                font: "inherit",
              }}
            />
          </label>
        )}

        <label
          style={{
            display: "flex",
            alignItems: "flex-start",
            gap: 10,
            marginTop: 14,
            color: "#334155",
            lineHeight: 1.5,
          }}
        >
          <input
            type="checkbox"
            checked={
              existingCoverRightsConfirmed
            }
            onChange={(event) =>
              setExistingCoverRightsConfirmed(
                event.target.checked
              )
            }
            style={{
              marginTop: 4,
            }}
          />

          <span>
            {isFR
              ? "Je confirme que je possède cette couverture ou que j’ai l’autorisation de l’utiliser."
              : "I confirm that I own this cover or have permission to use it."}
          </span>
        </label>

        {!assetManagementEnabled ? (
          <p
            className="ns-card-meta"
            style={{
              margin:
                "14px 0 0",
              color: "#92400e",
              fontWeight: 700,
            }}
          >
            {isFR
              ? "Enregistrez d’abord le NextScenes Book Blueprint pour activer la sélection de couverture."
              : "Save the NextScenes Book Blueprint first to enable cover selection."}
          </p>
        ) : null}

        {childrensBookAssetsError ? (
          <p
            role="alert"
            style={{
              margin:
                "14px 0 0",
              color: "#b91c1c",
              fontWeight: 700,
            }}
          >
            {childrensBookAssetsError}
          </p>
        ) : null}

        {existingCoverError ? (
          <p
            role="alert"
            style={{
              margin:
                "14px 0 0",
              color: "#b91c1c",
              fontWeight: 700,
            }}
          >
            {existingCoverError}
          </p>
        ) : null}

        {existingCoverMessage ? (
          <p
            role="status"
            style={{
              margin:
                "14px 0 0",
              color: "#166534",
              fontWeight: 700,
            }}
          >
            {existingCoverMessage}
          </p>
        ) : null}

        <div
          style={{
            display: "flex",
            gap: 10,
            flexWrap: "wrap",
            marginTop: 16,
          }}
        >
          <button
            className="ns-button ns-button-primary"
            type="button"
            disabled={
              assetBusy ||
              !assetManagementEnabled ||
              (
                coverSourceMode === "file" &&
                !selectedCoverFile
              )
            }
            onClick={
              coverSourceMode === "file"
                ? uploadSelectedCover
                : saveExistingCover
            }
          >
            {assetBusy
              ? (
                  isFR
                    ? "Enregistrement…"
                    : "Saving…"
                )
              : coverSourceMode === "file"
                ? (
                    activeFrontCover
                      ? (
                          isFR
                            ? "Téléverser et remplacer"
                            : "Upload and Replace"
                        )
                      : (
                          isFR
                            ? "Téléverser cette couverture"
                            : "Upload This Cover"
                        )
                  )
                : (
                    activeFrontCover
                      ? (
                          isFR
                            ? "Remplacer avec cette adresse"
                            : "Replace with This URL"
                        )
                      : (
                          isFR
                            ? "Utiliser cette adresse"
                            : "Use This URL"
                        )
                  )}
          </button>

          {activeFrontCover ? (
            <button
              className="ns-button ns-button-outline"
              type="button"
              disabled={assetBusy}
              onClick={
                requestExistingCoverRemoval
              }
            >
              {isFR
                ? "Retirer du projet"
                : "Remove from Project"}
            </button>
          ) : null}
        </div>

        <p
          className="ns-card-meta"
          style={{
            margin: "14px 0 0",
            lineHeight: 1.5,
          }}
        >
          {isFR
            ? "Les fichiers choisis sont téléversés dans le stockage local du pilote. Le remplacement et le retrait conservent les anciens enregistrements de manière non destructive."
            : "Chosen files are uploaded to the pilot’s local storage. Replacement and removal preserve earlier records non-destructively."}
        </p>

        {pendingCoverRemoval ? (
          <div
            style={{
              position: "fixed",
              inset: 0,
              zIndex: 1000,
              display: "grid",
              placeItems: "center",
              padding: 20,
              background:
                "rgba(15,23,42,0.58)",
            }}
          >
            <form
              role="alertdialog"
              aria-modal="true"
              aria-labelledby="remove-existing-cover-title"
              onSubmit={
                confirmExistingCoverRemoval
              }
              className="ns-card"
              style={{
                width:
                  "min(540px, 100%)",
                padding: 24,
                borderRadius: 18,
                border:
                  "1px solid rgba(220,38,38,0.30)",
                boxShadow:
                  "0 24px 70px rgba(15,23,42,0.28)",
              }}
            >
              <h3
                id="remove-existing-cover-title"
                className="ns-card-title"
                style={{
                  margin: "0 0 10px",
                }}
              >
                {isFR
                  ? "Retirer cette couverture ?"
                  : "Remove This Cover?"}
              </h3>

              <p
                className="ns-card-meta"
                style={{
                  margin: 0,
                  lineHeight: 1.65,
                }}
              >
                {isFR
                  ? "La couverture sera retirée du projet. Le fichier source et les anciens enregistrements ne seront pas supprimés."
                  : "The cover will be removed from the project. The source file and earlier records will not be deleted."}
              </p>

              <div
                style={{
                  display: "flex",
                  justifyContent:
                    "flex-end",
                  gap: 10,
                  flexWrap: "wrap",
                  marginTop: 22,
                }}
              >
                <button
                  className="ns-button ns-button-outline"
                  type="button"
                  disabled={assetBusy}
                  onClick={
                    cancelExistingCoverRemoval
                  }
                >
                  {isFR
                    ? "Annuler"
                    : "Cancel"}
                </button>

                <button
                  className="ns-button"
                  type="submit"
                  disabled={assetBusy}
                  style={{
                    background: "#b91c1c",
                    borderColor: "#b91c1c",
                    color: "#ffffff",
                  }}
                >
                  {assetBusy
                    ? (
                        isFR
                          ? "Retrait…"
                          : "Removing…"
                      )
                    : (
                        isFR
                          ? "Retirer la couverture"
                          : "Remove Cover"
                      )}
                </button>
              </div>
            </form>
          </div>
        ) : null}
      </article>
    );
  }

  function renderFrontCoverPreparationGuide() {
    const rawTrim =
      String(
        projectPlan
          ?.visualFormat
          ?.trimSize ||
        ""
      ).trim();

    const readableTrim =
      rawTrim.replace(
        /_/g,
        " "
      );

    const trimMatch =
      readableTrim.match(
        /(\d+(?:\.\d+)?)\s*x\s*(\d+(?:\.\d+)?)/i
      );

    const usesInches =
      /(?:^|[\s_])in(?:$|[\s_])/i.test(
        rawTrim
      );

    const widthInches =
      trimMatch
        ? Number(trimMatch[1])
        : null;

    const heightInches =
      trimMatch
        ? Number(trimMatch[2])
        : null;

    const hasDimensions =
      Number.isFinite(widthInches) &&
      widthInches > 0 &&
      Number.isFinite(heightInches) &&
      heightInches > 0;

    const trimLabel =
      hasDimensions
        ? `${widthInches} × ${heightInches}${usesInches ? " in" : ""}`
        : displayValue(rawTrim);

    const shapeLabel =
      hasDimensions
        ? (
            Math.abs(
              widthInches -
              heightInches
            ) < 0.001
              ? (
                  isFR
                    ? "Carré (1:1)"
                    : "Square (1:1)"
                )
              : widthInches <
                  heightInches
                ? (
                    isFR
                      ? "Portrait"
                      : "Portrait"
                  )
                : (
                    isFR
                      ? "Paysage"
                      : "Landscape"
                  )
          )
        : "";

    const trimPixelLabel =
      hasDimensions &&
      usesInches
        ? `${Math.round(
            widthInches * 300
          )} × ${Math.round(
            heightInches * 300
          )} px`
        : "";

    const bleedLabel =
      displayValue(
        projectPlan
          ?.visualFormat
          ?.bleed
      );

    return (
      <section
        className="ns-card ns-nbb-feature-panel"
        aria-label={
          isFR
            ? "Préparer votre couverture avant"
            : "Prepare your front cover"
        }
        style={{
          padding:
            "clamp(16px, 2.4vw, 22px)",
          borderRadius: 18,
          border:
            "1px solid rgba(22,101,52,0.24)",
          background:
            "linear-gradient(145deg, #f8fff9, #ffffff)",
          marginBottom: 18,
        }}
      >
        <div
          style={{
            display: "flex",
            justifyContent:
              "space-between",
            alignItems:
              "flex-start",
            gap: 18,
            flexWrap: "wrap",
          }}
        >
          <div>
            <div
              style={{
                fontSize: 12,
                fontWeight: 900,
                letterSpacing:
                  "0.08em",
                textTransform:
                  "uppercase",
                color: "#166534",
                marginBottom: 5,
              }}
            >
              {isFR
                ? "Guide de préparation"
                : "Cover preparation"}
            </div>

            <strong
              style={{
                display: "block",
                fontSize: 18,
              }}
            >
              {isFR
                ? "Préparez votre couverture avant"
                : "Prepare Your Front Cover"}
            </strong>
          </div>

          <div
            style={{
              textAlign: "right",
              lineHeight: 1.45,
            }}
          >
            <strong>
              {isFR
                ? "Format sélectionné : "
                : "Selected trim: "}
              {trimLabel}
            </strong>

            {shapeLabel ? (
              <div
                className="ns-card-meta"
                style={{
                  marginTop: 2,
                }}
              >
                {shapeLabel}
              </div>
            ) : null}
          </div>
        </div>

        <p
          className="ns-card-meta"
          style={{
            margin:
              "12px 0 0",
            lineHeight: 1.6,
            maxWidth: 820,
          }}
        >
          {isFR
            ? "Préparez la couverture dans les mêmes proportions que le format sélectionné. Gardez le titre, le nom de l’auteur, les visages et les autres éléments importants à bonne distance des bords."
            : "Prepare the cover in the same proportions as the selected trim. Keep the title, author name, faces, and other important elements comfortably away from the edges."}
        </p>

        <div
          style={{
            display: "flex",
            gap: 10,
            flexWrap: "wrap",
            marginTop: 12,
          }}
        >
          {trimPixelLabel ? (
            <span
              style={{
                padding:
                  "7px 10px",
                borderRadius: 999,
                background:
                  "#f0fdf4",
                border:
                  "1px solid rgba(22,101,52,0.22)",
                fontSize: 13,
                fontWeight: 700,
              }}
            >
              {isFR
                ? "Équivalent du format à 300 PPP : "
                : "Trim-equivalent at 300 DPI: "}
              {trimPixelLabel}
            </span>
          ) : null}

          <span
            style={{
              padding:
                "7px 10px",
              borderRadius: 999,
              background:
                "#fff7ed",
              border:
                "1px solid rgba(234,88,12,0.20)",
              fontSize: 13,
              fontWeight: 700,
            }}
          >
            {isFR
              ? "Fond perdu : "
              : "Bleed: "}
            {bleedLabel}
          </span>
        </div>

        <details
          style={{
            marginTop: 14,
          }}
        >
          <summary
            style={{
              cursor: "pointer",
              color: "#166534",
              fontWeight: 800,
            }}
          >
            {isFR
              ? "Voir le guide des dimensions de couverture"
              : "View Cover Size Guide"}
          </summary>

          <div
            className="ns-card-meta"
            style={{
              marginTop: 10,
              lineHeight: 1.65,
              maxWidth: 860,
            }}
          >
            <p
              style={{
                margin:
                  "0 0 8px",
              }}
            >
              {isFR
                ? "Le format indiqué ci-dessus correspond à la taille finale du livre après coupe."
                : "The trim shown above is the finished book size after cutting."}
            </p>

            <p
              style={{
                margin:
                  "0 0 8px",
              }}
            >
              {isFR
                ? "Une résolution de 300 PPP est recommandée pour une image de qualité d’impression. La valeur en pixels ci-dessus décrit uniquement la zone du format fini."
                : "300 DPI is recommended for print-quality artwork. The pixel figure above describes the trim area only."}
            </p>

            <p
              style={{
                margin:
                  "0 0 8px",
              }}
            >
              {isFR
                ? "Si un fond perdu est prévu, prolongez l’arrière-plan au-delà du bord de coupe. Les dimensions extérieures exactes avec fond perdu seront confirmées par le profil de production."
                : "If bleed is planned, extend background artwork beyond the trim edge. The exact bleed-ready outer dimensions will be confirmed by the production profile."}
            </p>

            <p
              style={{
                margin: 0,
              }}
            >
              {isFR
                ? "NextScenes vérifiera la couverture avant la production et bloquera une image qui nécessiterait une coupe excessive."
                : "NextScenes will check the uploaded cover before production and block a cover that would be cropped too heavily."}
            </p>
          </div>
        </details>
      </section>
    );
  }


  function displayValue(value) {
    const normalized =
      String(value ?? "").trim();

    if (!normalized) {
      return isFR
        ? "Non renseigné"
        : "Not provided";
    }

    const labels = {
      front_only: isFR
        ? "Couverture avant uniquement"
        : "Front cover only",
      full_print_wrap: isFR
        ? "Couverture imprimée complète"
        : "Full print wrap",
      digital_cover: isFR
        ? "Couverture numérique"
        : "Digital cover",
      en: "English",
      fr: "Français",
      bilingual: isFR
        ? "Bilingue"
        : "Bilingual",
    };

    return labels[normalized] || normalized;
  }

  const inheritedRows = [
    [
      isFR ? "Orientation" : "Orientation",
      projectPlan.visualFormat.orientation,
    ],
    [
      isFR
        ? "Format ou dimensions"
        : "Trim size or dimensions",
      projectPlan.visualFormat.trimSize,
    ],
    [
      isFR ? "Fond perdu" : "Bleed",
      projectPlan.visualFormat.bleed,
    ],
    [
      isFR
        ? "Format de publication"
        : "Publication format",
      projectPlan.visualFormat.publicationFormat,
    ],
  ];

  const reviewRows = [
    [
      isFR ? "Type de couverture" : "Cover type",
      coverPlan.coverType,
    ],
    [
      isFR
        ? "Langue de l’édition"
        : "Edition language",
      coverPlan.editionLanguage,
    ],
    [
      isFR ? "Titre affiché" : "Displayed title",
      coverPlan.title,
    ],
    [
      isFR
        ? "Sous-titre affiché"
        : "Displayed subtitle",
      coverPlan.subtitle,
    ],
    [
      isFR
        ? "Nom d’auteur affiché"
        : "Displayed author name",
      coverPlan.authorName,
    ],
    [
      isFR
        ? "Crédit de l’illustrateur"
        : "Illustrator credit",
      coverPlan.illustratorCredit,
    ],
    [
      isFR
        ? "Autres crédits"
        : "Other contributor credits",
      coverPlan.contributorCredits,
    ],
    [
      isFR
        ? "Concept visuel"
        : "Visual concept",
      coverPlan.visualBrief,
    ],
    [
      isFR
        ? "Personnages ou objets principaux"
        : "Main characters or objects",
      coverPlan.featuredElements,
    ],
    [
      isFR
        ? "Cadre et atmosphère culturelle"
        : "Setting and cultural atmosphere",
      coverPlan.settingAtmosphere,
    ],
    [
      isFR
        ? "Ambiance des couleurs"
        : "Colour mood",
      coverPlan.colourMood,
    ],
    [
      isFR
        ? "Style d’illustration"
        : "Illustration style",
      coverPlan.illustrationStyle,
    ],
    [
      isFR
        ? "Placement du texte"
        : "Cover text placement",
      coverPlan.textPlacement,
    ],
    [
      isFR
        ? "Texte de quatrième de couverture"
        : "Back-cover description",
      coverPlan.backCoverBlurb,
    ],
    [
      isFR ? "Texte du dos" : "Spine text",
      coverPlan.spineText,
    ],
    [
      isFR
        ? "Droits et remplacement"
        : "Rights and replacement note",
      coverPlan.rightsNote,
    ],
  ];

  if (reviewingCover) {
    return (
      <section
        aria-label={
          isFR
            ? "Vérification du plan de couverture"
            : "Review cover plan"
        }
      >
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "flex-start",
            flexWrap: "wrap",
            gap: 16,
            marginBottom: 18,
          }}
        >
          <div>
            <div
              style={{
                fontSize: 12,
                fontWeight: 900,
                letterSpacing: "0.12em",
                textTransform: "uppercase",
                color: "#b45309",
              }}
            >
              {isFR
                ? "Brouillon local de couverture"
                : "Local cover draft"}
            </div>

            <h2
              className="ns-section-title"
              style={{ margin: "5px 0 8px" }}
            >
              {isFR
                ? "Vérifiez le plan de couverture"
                : "Review Cover Plan"}
            </h2>

            <p
              className="ns-card-meta"
              style={{ margin: 0 }}
            >
              {isFR
                ? "Vérifiez les choix créatifs et techniques avant de revenir au plan du projet."
                : "Review the creative and technical choices before returning to the project plan."}
            </p>
          </div>

          <button
            className="ns-button ns-button-outline"
            type="button"
            onClick={onExit}
          >
            {isFR
              ? "Retour à Mes projets"
              : "Return to My Projects"}
          </button>
        </div>

        <div
          className="ns-card"
          style={{
            padding: 16,
            marginBottom: 18,
            borderRadius: 16,
            background: "#fff7ed",
            border:
              "1px solid rgba(234,88,12,0.25)",
          }}
        >
          <strong>
            {activeFrontCover
              ? (
                  isFR
                    ? "Une couverture existante est associée à ce projet"
                    : "An existing cover is associated with this project"
                )
              : (
                  isFR
                    ? "Aucune couverture n’a encore été sélectionnée"
                    : "No cover has been selected yet"
                )}
          </strong>

          <p
            className="ns-card-meta"
            style={{
              margin: "7px 0 0",
              lineHeight: 1.55,
            }}
          >
            {activeFrontCover
              ? (
                  isFR
                    ? "La couverture sélectionnée reste distincte du plan créatif textuel et peut être remplacée ou retirée sans supprimer son fichier source."
                    : "The selected cover remains separate from the textual Creative Cover Plan and can be replaced or removed without deleting its source file."
                )
              : (
                  isFR
                    ? "Vous pouvez conserver uniquement le plan textuel ou revenir à l’écran de modification pour sélectionner une couverture existante autorisée."
                    : "You may keep only the textual plan or return to the editing screen to select an authorised existing cover."
                )}
          </p>

          {previewCoverUrl ||
          (
            coverPlan.coverType ===
              "full_print_wrap" &&
            activeBackCover
          ) ? (
            <div
              style={{
                display: "grid",
                gridTemplateColumns:
                  "repeat(auto-fit, minmax(220px, 1fr))",
                gap: 18,
                marginTop: 16,
              }}
            >
              {previewCoverUrl ? (
                <div
                  style={{
                    padding: 14,
                    borderRadius: 12,
                    background: "#ffffff",
                    border:
                      "1px solid rgba(148,163,184,0.35)",
                  }}
                >
                  <strong
                    style={{
                      display: "block",
                      marginBottom: 10,
                      textAlign: "center",
                    }}
                  >
                    {isFR
                      ? "Couverture avant"
                      : "Front cover"}
                  </strong>

                  <img
                    src={previewCoverUrl}
                    alt={
                      isFR
                        ? "Couverture avant actuellement associée au projet"
                        : "Front cover currently associated with the project"
                    }
                    style={{
                      display: "block",
                      width: "100%",
                      maxWidth: 280,
                      maxHeight: 400,
                      objectFit: "contain",
                      margin: "0 auto",
                      borderRadius: 10,
                    }}
                  />
                </div>
              ) : null}

              {coverPlan.coverType ===
                "full_print_wrap" &&
              activeBackCover ? (
                <div
                  style={{
                    padding: 14,
                    borderRadius: 12,
                    background: "#ffffff",
                    border:
                      "1px solid rgba(148,163,184,0.35)",
                  }}
                >
                  <strong
                    style={{
                      display: "block",
                      marginBottom: 10,
                      textAlign: "center",
                    }}
                  >
                    {isFR
                      ? "Image de quatrième de couverture"
                      : "Back-cover image"}
                  </strong>

                  {persistedBackCoverPreviewLoading ? (
                    <div
                      role="status"
                      className="ns-card-meta"
                      style={{
                        textAlign: "center",
                        fontWeight: 800,
                      }}
                    >
                      {isFR
                        ? "Chargement de l’image de quatrième de couverture enregistrée…"
                        : "Loading the saved back-cover image…"}
                    </div>
                  ) : null}

                  {persistedBackCoverPreviewError ? (
                    <div
                      role="alert"
                      style={{
                        color: "#b91c1c",
                        fontWeight: 800,
                        textAlign: "center",
                      }}
                    >
                      {persistedBackCoverPreviewError}
                    </div>
                  ) : null}

                  {backCoverPreviewUrl ? (
                    <img
                      src={backCoverPreviewUrl}
                      alt={
                        isFR
                          ? "Image de quatrième de couverture actuellement associée au projet"
                          : "Back-cover image currently associated with the project"
                      }
                      style={{
                        display: "block",
                        width: "100%",
                        maxWidth: 280,
                        maxHeight: 400,
                        objectFit: "contain",
                        margin: "0 auto",
                        borderRadius: 10,
                      }}
                    />
                  ) : null}
                </div>
              ) : null}
            </div>
          ) : null}
        </div>

        <div
          style={{
            display: "grid",
            gap: 16,
          }}
        >
          <article
            className="ns-card ns-nbb-feature-panel"
            style={{
              padding: 20,
              borderRadius: 18,
              border:
                "1px solid rgba(234,88,12,0.20)",
            }}
          >
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                flexWrap: "wrap",
                gap: 12,
                marginBottom: 14,
              }}
            >
              <h3
                className="ns-card-title"
                style={{ margin: 0 }}
              >
                {isFR
                  ? "Plan créatif de couverture"
                  : "Creative Cover Plan"}
              </h3>

              <button
                className="ns-button ns-button-outline"
                type="button"
                onClick={() =>
                  setReviewingCover(false)
                }
              >
                {isFR
                  ? "Modifier le plan"
                  : "Edit Cover Plan"}
              </button>
            </div>

            <dl
              style={{
                display: "grid",
                gridTemplateColumns:
                  "repeat(auto-fit, minmax(220px, 1fr))",
                gap: 12,
                margin: 0,
              }}
            >
              {reviewRows.map(([label, value]) => (
                <div
                  key={label}
                  className="ns-nbb-inner-panel"
                  style={{
                    padding: 12,
                    borderRadius: 12,
                    background: "#f8fafc",
                  }}
                >
                  <dt
                    style={{
                      fontSize: 12,
                      fontWeight: 900,
                      color: "#64748b",
                      marginBottom: 5,
                    }}
                  >
                    {label}
                  </dt>

                  <dd
                    style={{
                      margin: 0,
                      fontWeight: 700,
                      color: "#1e293b",
                      whiteSpace: "pre-wrap",
                      overflowWrap: "anywhere",
                    }}
                  >
                    {displayValue(value)}
                  </dd>
                </div>
              ))}
            </dl>
          </article>

          <article
            className="ns-card ns-nbb-feature-panel"
            style={{
              padding: 20,
              borderRadius: 18,
              border:
                "1px solid rgba(234,88,12,0.20)",
            }}
          >
            <h3
              className="ns-card-title"
              style={{ margin: "0 0 14px" }}
            >
              {isFR
                ? "Caractéristiques héritées du livre"
                : "Inherited Book Specifications"}
            </h3>

            <dl
              style={{
                display: "grid",
                gridTemplateColumns:
                  "repeat(auto-fit, minmax(220px, 1fr))",
                gap: 12,
                margin: 0,
              }}
            >
              {inheritedRows.map(([label, value]) => (
                <div
                  key={label}
                  className="ns-nbb-inner-panel"
                  style={{
                    padding: 12,
                    borderRadius: 12,
                    background: "#f8fafc",
                  }}
                >
                  <dt
                    style={{
                      fontSize: 12,
                      fontWeight: 900,
                      color: "#64748b",
                      marginBottom: 5,
                    }}
                  >
                    {label}
                  </dt>

                  <dd
                    style={{
                      margin: 0,
                      fontWeight: 700,
                      color: "#1e293b",
                    }}
                  >
                    {displayValue(value)}
                  </dd>
                </div>
              ))}
            </dl>
          </article>
        </div>

        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            gap: 12,
            flexWrap: "wrap",
            marginTop: 18,
          }}
        >
          <button
            className="ns-button ns-button-outline"
            type="button"
            onClick={onReturn}
          >
            {returnToWholeBookReview
              ? (
                  isFR
                    ? "Retour à la révision complète du livre"
                    : "Return to Whole Book Review"
                )
              : (
                  isFR
                    ? "Retour au NextScenes Book Blueprint"
                    : "Return to NextScenes Book Blueprint"
                )}
          </button>

          <button
            className="ns-button ns-button-primary"
            type="button"
            disabled
            title={
              isFR
                ? "L’enregistrement permanent n’est pas encore autorisé."
                : "Permanent saving is not yet authorised."
            }
          >
            {isFR
              ? "Enregistrement non activé"
              : "Saving not enabled"}
          </button>
        </div>
      </section>
    );
  }

  return (
    <section
      aria-label={
        isFR
          ? "Planification de la couverture"
          : "Cover planning"
      }
    >
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-start",
          flexWrap: "wrap",
          gap: 16,
          marginBottom: 18,
        }}
      >
        <div>
          <div
            style={{
              fontSize: 12,
              fontWeight: 900,
              letterSpacing: "0.12em",
              textTransform: "uppercase",
              color: "#b45309",
            }}
          >
            {isFR
              ? "Planification de la couverture"
              : "Cover planning"}
          </div>

          <h2
            className="ns-section-title"
            style={{ margin: "5px 0 8px" }}
          >
            {isFR
              ? "Planifiez la couverture"
              : "Plan the Cover"}
          </h2>

          <p
            className="ns-card-meta"
            style={{ margin: 0 }}
          >
            {isFR
              ? "Planifiez une nouvelle couverture ou choisissez une couverture existante autorisée depuis votre appareil. Une adresse d’image sécurisée reste également disponible."
              : "Plan a new cover or choose an authorised existing cover from your device. A secure image address also remains available."}
          </p>
        </div>

        <button
          className="ns-button ns-button-outline"
          type="button"
          onClick={onExit}
        >
          {isFR
            ? "Retour à Mes projets"
            : "Return to My Projects"}
        </button>
      </div>

      {renderFrontCoverPreparationGuide()}

      {renderExistingCoverPanel()}

      <div
        className="ns-card ns-nbb-form-panel"
        style={{
          padding: "clamp(18px, 3vw, 26px)",
          borderRadius: 20,
          border:
            "1px solid rgba(234,88,12,0.22)",
          background:
            "linear-gradient(145deg, #ffffff, #fffaf3)",
        }}
      >
        <div style={fieldGrid}>
          <SelectField
            label={
              isFR
                ? "Type de couverture"
                : "Cover type"
            }
            value={coverPlan.coverType}
            onChange={(value) =>
              update("coverType", value)
            }
            options={[
              {
                value: "front_only",
                label: isFR
                  ? "Couverture avant uniquement"
                  : "Front cover only",
              },
              {
                value: "full_print_wrap",
                label: isFR
                  ? "Couverture imprimée complète"
                  : "Full print wrap",
              },
              {
                value: "digital_cover",
                label: isFR
                  ? "Couverture numérique"
                  : "Digital cover",
              },
            ]}
          />

          <SelectField
            label={
              isFR
                ? "Langue de l’édition"
                : "Edition language"
            }
            value={coverPlan.editionLanguage}
            onChange={(value) =>
              update("editionLanguage", value)
            }
            options={[
              {
                value: "en",
                label: "English",
              },
              {
                value: "fr",
                label: "Français",
              },
              {
                value: "bilingual",
                label: isFR
                  ? "Bilingue"
                  : "Bilingual",
              },
            ]}
          />

          {!activeFrontCover ? (
            <>
              <Field
                label={
                  isFR
                    ? "Titre affiché"
                    : "Displayed title"
                }
                value={coverPlan.title}
                onChange={(value) =>
                  update("title", value)
                }
              />

              <Field
                label={
                  isFR
                    ? "Sous-titre affiché"
                    : "Displayed subtitle"
                }
                value={coverPlan.subtitle}
                onChange={(value) =>
                  update("subtitle", value)
                }
              />

              <Field
                label={
                  isFR
                    ? "Nom d’auteur affiché"
                    : "Displayed author name"
                }
                value={coverPlan.authorName}
                onChange={(value) =>
                  update("authorName", value)
                }
              />
            </>
          ) : null}

          <Field
            label={
              isFR
                ? "Crédit de l’illustrateur"
                : "Illustrator credit"
            }
            value={coverPlan.illustratorCredit}
            onChange={(value) =>
              update("illustratorCredit", value)
            }
          />

          <Field
            label={
              isFR
                ? "Autres crédits"
                : "Other contributor credits"
            }
            value={coverPlan.contributorCredits}
            onChange={(value) =>
              update("contributorCredits", value)
            }
          />

          <Field
            label={
              isFR
                ? "Personnages ou objets principaux"
                : "Main characters or objects"
            }
            value={coverPlan.featuredElements}
            onChange={(value) =>
              update("featuredElements", value)
            }
          />

          <Field
            label={
              isFR
                ? "Ambiance des couleurs"
                : "Colour mood"
            }
            value={coverPlan.colourMood}
            onChange={(value) =>
              update("colourMood", value)
            }
          />

          <Field
            label={
              isFR
                ? "Style d’illustration"
                : "Illustration style"
            }
            value={coverPlan.illustrationStyle}
            onChange={(value) =>
              update("illustrationStyle", value)
            }
          />

          <Field
            label={
              isFR
                ? "Placement du texte"
                : "Cover text placement"
            }
            value={coverPlan.textPlacement}
            onChange={(value) =>
              update("textPlacement", value)
            }
          />
        </div>

        <div
          style={{
            display: "grid",
            gap: 16,
            marginTop: 16,
          }}
        >
          <Field
            multiline
            label={
              isFR
                ? "Concept ou brief visuel"
                : "Cover concept or visual brief"
            }
            value={coverPlan.visualBrief}
            onChange={(value) =>
              update("visualBrief", value)
            }
          />

          <Field
            multiline
            label={
              isFR
                ? "Cadre et atmosphère culturelle"
                : "Setting and cultural atmosphere"
            }
            value={coverPlan.settingAtmosphere}
            onChange={(value) =>
              update("settingAtmosphere", value)
            }
          />

          {coverPlan.coverType ===
            "full_print_wrap" && (
            <>
              <div
                style={{
                  padding: 16,
                  border:
                    "1px solid #cbd5e1",
                  borderRadius: 12,
                  background: "#ffffff",
                }}
              >
                <strong>
                  {isFR
                    ? "Image de quatrième de couverture (Facultatif)"
                    : "Back-cover image (Optional)"}
                </strong>

                <p
                  className="ns-card-meta"
                  style={{
                    margin: "6px 0 14px",
                    lineHeight: 1.5,
                  }}
                >
                  {isFR
                    ? "Ajoutez, si vous le souhaitez, une illustration ou une autre image destinée à la quatrième de couverture. La photo d’auteur est gérée séparément ci-dessous."
                    : "Optionally add an illustration or other artwork intended for the back cover. The author photo is managed separately below."}
                </p>

                <label
                  style={{
                    display: "grid",
                    gap: 6,
                    margin: "0 0 14px",
                  }}
                >
                  <span
                    style={{
                      fontWeight: 800,
                    }}
                  >
                    {isFR
                      ? "Utilisation de l’image de quatrième de couverture"
                      : "Back-cover image use"}
                  </span>

                  <select
                    value={
                      [
                        "none",
                        "artwork",
                        "background",
                      ].includes(
                        coverPlan
                          .backCoverImageMode
                      )
                        ? coverPlan
                            .backCoverImageMode
                        : activeBackCover
                          ? "artwork"
                          : "none"
                    }
                    onChange={(event) =>
                      update(
                        "backCoverImageMode",
                        event.target.value
                      )
                    }
                    style={{
                      width: "100%",
                      minHeight: 42,
                      padding:
                        "8px 10px",
                      borderRadius: 8,
                      border:
                        "1px solid #cbd5e1",
                      background:
                        "#ffffff",
                    }}
                  >
                    <option value="none">
                      {isFR
                        ? "Aucune image"
                        : "No image"}
                    </option>

                    <option value="artwork">
                      {isFR
                        ? "Illustration"
                        : "Artwork"}
                    </option>

                    <option value="background">
                      {isFR
                        ? "Image d’arrière-plan"
                        : "Background image"}
                    </option>
                  </select>

                  <span
                    className="ns-card-meta"
                    style={{
                      lineHeight: 1.45,
                    }}
                  >
                    {isFR
                      ? "Une illustration est placée comme élément distinct. Une image d’arrière-plan remplit la quatrième de couverture derrière le titre, le texte et la photo d’auteur. Aucune image produit une composition typographique propre."
                      : "Artwork is placed as a separate visual element. A background image fills the back panel behind the title, blurb, and author photo. No image produces a clean text-led composition."}
                  </span>
                </label>

                <label
                  style={{
                    display: "grid",
                    gap: 6,
                    margin: "0 0 14px",
                  }}
                >
                  <span
                    style={{
                      fontWeight: 800,
                    }}
                  >
                    {isFR
                      ? "Rôle de l’illustration de quatrième de couverture (Facultatif)"
                      : "Back-cover artwork role (Optional)"}
                  </span>

                  <select
                    value={
                      [
                        "illustration",
                        "other",
                      ].includes(
                        coverPlan
                          .backCoverImageRole
                      )
                        ? coverPlan
                            .backCoverImageRole
                        : ""
                    }
                    onChange={(event) =>
                      update(
                        "backCoverImageRole",
                        event.target.value
                      )
                    }
                    style={{
                      width: "100%",
                      minHeight: 42,
                      padding:
                        "8px 10px",
                      borderRadius: 8,
                      border:
                        "1px solid #cbd5e1",
                      background:
                        "#ffffff",
                    }}
                  >
                    <option value="">
                      {isFR
                        ? "Non précisé"
                        : "Not specified"}
                    </option>

                     <option value="illustration">
                      {isFR
                        ? "Illustration"
                        : "Illustration"}
                    </option>

                    <option value="other">
                      {isFR
                        ? "Autre image"
                        : "Other image"}
                    </option>
                  </select>

                  <span
                    className="ns-card-meta"
                    style={{
                      lineHeight: 1.45,
                    }}
                  >
                    {isFR
                      ? "Ce choix décrit uniquement l’illustration de quatrième de couverture. La photo d’auteur possède désormais sa propre ressource indépendante."
                      : "This describes only the back-cover artwork. The author photo now has its own independent asset."}
                  </span>
                </label>

                {activeBackCover ? (
                  <div
                    style={{
                      marginBottom: 16,
                      padding: 14,
                      borderRadius: 12,
                      border:
                        "1px solid rgba(22,101,52,0.24)",
                      background:
                        "rgba(240,253,244,0.72)",
                    }}
                  >
                    <strong>
                      {isFR
                        ? "Image de quatrième de couverture actuellement sélectionnée"
                        : "Current Back-cover Image"}
                    </strong>

                    <p
                      className="ns-card-meta"
                      style={{
                        margin:
                          "6px 0 0",
                        lineHeight: 1.5,
                      }}
                    >
                      {isFR
                        ? "Cette image est enregistrée comme ressource active de quatrième de couverture du projet. Téléversez un nouveau fichier ci-dessous uniquement si vous souhaitez la remplacer."
                        : "This image is stored as the project’s active back-cover asset. Upload a new file below only if you want to replace it."}
                    </p>

                    {persistedBackCoverPreviewLoading ? (
                      <div
                        role="status"
                        className="ns-card-meta"
                        style={{
                          marginTop: 12,
                          fontWeight: 800,
                        }}
                      >
                        {isFR
                          ? "Chargement de l’image de quatrième de couverture enregistrée…"
                          : "Loading the saved back-cover image…"}
                      </div>
                    ) : null}

                    {persistedBackCoverPreviewError ? (
                      <div
                        role="alert"
                        style={{
                          marginTop: 12,
                          color: "#b91c1c",
                          fontWeight: 800,
                        }}
                      >
                        {persistedBackCoverPreviewError}
                      </div>
                    ) : null}

                    {backCoverPreviewUrl ? (
                      <div
                        style={{
                          marginTop: 12,
                          display: "grid",
                          placeItems: "center",
                          padding: 12,
                          borderRadius: 12,
                          background: "#ffffff",
                          border:
                            "1px solid rgba(148,163,184,0.35)",
                        }}
                      >
                        <img
                          src={
                            backCoverPreviewUrl
                          }
                          alt={
                            isFR
                              ? "Aperçu de l’image de quatrième de couverture sélectionnée"
                              : "Preview of the selected back-cover image"
                          }
                          style={{
                            display: "block",
                            width: "100%",
                            maxWidth: 280,
                            maxHeight: 380,
                            objectFit:
                              "contain",
                            borderRadius: 10,
                          }}
                        />
                      </div>
                    ) : null}
                  </div>
                ) : null}

                <input
                  type="file"
                  accept="image/jpeg,image/png,image/webp"
                  disabled={
                    !assetManagementEnabled ||
                    backCoverSubmitting
                  }
                  onChange={
                    selectBackCoverFile
                  }
                />

                <p
                  className="ns-card-meta"
                  style={{
                    margin: "7px 0 0",
                  }}
                >
                  {isFR
                    ? "JPG, PNG ou WEBP. Taille maximale : 5 Mo."
                    : "JPG, PNG, or WEBP. Maximum size: 5 MB."}
                </p>

                {selectedBackCoverFile ? (
                  <p
                    style={{
                      margin: "8px 0 0",
                      fontWeight: 700,
                      color: "#166534",
                    }}
                  >
                    {selectedBackCoverFile.name}
                  </p>
                ) : null}

                <label
                  style={{
                    display: "flex",
                    alignItems: "flex-start",
                    gap: 9,
                    marginTop: 12,
                    lineHeight: 1.5,
                  }}
                >
                  <input
                    type="checkbox"
                    checked={
                      backCoverRightsConfirmed
                    }
                    onChange={(event) =>
                      setBackCoverRightsConfirmed(
                        event.target.checked
                      )
                    }
                  />

                  <span>
                    {isFR
                      ? "Je confirme que je possède cette image ou que j’ai l’autorisation de l’utiliser."
                      : "I confirm that I own this image or have permission to use it."}
                  </span>
                </label>

                {backCoverError ? (
                  <p
                    role="alert"
                    style={{
                      margin: "10px 0 0",
                      color: "#b91c1c",
                      fontWeight: 700,
                    }}
                  >
                    {backCoverError}
                  </p>
                ) : null}

                {backCoverMessage ? (
                  <p
                    role="status"
                    style={{
                      margin: "10px 0 0",
                      color: "#166534",
                      fontWeight: 700,
                    }}
                  >
                    {backCoverMessage}
                  </p>
                ) : null}

                <button
                  className="ns-button ns-button-primary"
                  type="button"
                  disabled={
                    !assetManagementEnabled ||
                    backCoverSubmitting ||
                    !selectedBackCoverFile
                  }
                  onClick={
                    uploadSelectedBackCover
                  }
                  style={{
                    marginTop: 14,
                  }}
                >
                  {backCoverSubmitting
                    ? (
                        isFR
                          ? "Enregistrement…"
                          : "Saving…"
                      )
                    : activeBackCover
                      ? (
                          isFR
                            ? "Téléverser et remplacer"
                            : "Upload and Replace"
                        )
                      : (
                          isFR
                            ? "Téléverser l’image"
                            : "Upload Back-cover Image"
                        )}
                </button>
              </div>

              <div
                style={{
                  padding: 16,
                  border:
                    "1px solid #cbd5e1",
                  borderRadius: 12,
                  background: "#ffffff",
                }}
              >
                <strong>
                  {isFR
                    ? "Photo d’auteur (Facultatif)"
                    : "Author photo (Optional)"}
                </strong>

                <p
                  className="ns-card-meta"
                  style={{
                    margin: "6px 0 14px",
                    lineHeight: 1.5,
                  }}
                >
                  {isFR
                    ? "Ajoutez une photo d’auteur distincte de l’illustration de quatrième de couverture."
                    : "Add an author photo separately from the back-cover artwork."}
                </p>

                {activeAuthorPhoto ? (
                  <div
                    style={{
                      marginBottom: 14,
                      padding: 12,
                      borderRadius: 10,
                      background:
                        "rgba(240,253,244,0.72)",
                      border:
                        "1px solid rgba(22,101,52,0.24)",
                    }}
                  >
                    <strong>
                      {isFR
                        ? "Photo d’auteur actuelle"
                        : "Current Author Photo"}
                    </strong>

                    <p
                      className="ns-card-meta"
                      style={{
                        margin: "6px 0 0",
                        overflowWrap:
                          "anywhere",
                      }}
                    >
                      {
                        activeAuthorPhoto
                          .fileName ||
                        (
                          isFR
                            ? "Photo d’auteur enregistrée"
                            : "Saved author photo"
                        )
                      }
                    </p>

                    <p
                      className="ns-card-meta"
                      style={{
                        margin: "5px 0 0",
                      }}
                    >
                      {isFR
                        ? "Elle est enregistrée séparément de l’image de quatrième de couverture."
                        : "It is stored separately from the back-cover artwork."}
                    </p>

                    {persistedAuthorPhotoPreviewLoading ? (
                      <div
                        role="status"
                        className="ns-card-meta"
                        style={{
                          marginTop: 12,
                          fontWeight: 800,
                        }}
                      >
                        {isFR
                          ? "Chargement de la photo d’auteur enregistrée…"
                          : "Loading the saved author photo…"}
                      </div>
                    ) : null}

                    {persistedAuthorPhotoPreviewError ? (
                      <div
                        role="alert"
                        style={{
                          marginTop: 12,
                          color: "#b91c1c",
                          fontWeight: 800,
                        }}
                      >
                        {
                          persistedAuthorPhotoPreviewError
                        }
                      </div>
                    ) : null}

                    {authorPhotoPreviewUrl ? (
                      <div
                        style={{
                          marginTop: 12,
                          display: "grid",
                          placeItems: "center",
                          padding: 12,
                          borderRadius: 12,
                          background: "#ffffff",
                          border:
                            "1px solid rgba(148,163,184,0.35)",
                        }}
                      >
                        <img
                          src={
                            authorPhotoPreviewUrl
                          }
                          alt={
                            isFR
                              ? "Aperçu de la photo d’auteur sélectionnée"
                              : "Preview of the selected author photo"
                          }
                          style={{
                            display: "block",
                            width: "100%",
                            maxWidth: 180,
                            maxHeight: 220,
                            objectFit:
                              "contain",
                            borderRadius: 10,
                          }}
                        />
                      </div>
                    ) : null}

                    {!pendingAuthorPhotoRemoval ? (
                      <button
                        className="ns-button ns-button-outline"
                        type="button"
                        disabled={
                          authorPhotoSubmitting
                        }
                        onClick={
                          requestAuthorPhotoRemoval
                        }
                        style={{
                          marginTop: 12,
                        }}
                      >
                        {isFR
                          ? "Retirer la photo d’auteur"
                          : "Remove Author Photo"}
                      </button>
                    ) : (
                      <div
                        style={{
                          marginTop: 12,
                          padding: 12,
                          borderRadius: 10,
                          border:
                            "1px solid rgba(185,28,28,0.25)",
                          background:
                            "rgba(254,242,242,0.72)",
                        }}
                      >
                        <p
                          className="ns-card-meta"
                          style={{
                            margin: 0,
                            lineHeight: 1.5,
                          }}
                        >
                          {isFR
                            ? "Retirer cette photo du projet ? Le fichier source ne sera pas supprimé."
                            : "Remove this author photo from the project? The source file will not be deleted."}
                        </p>

                        <div
                          style={{
                            display: "flex",
                            gap: 10,
                            flexWrap: "wrap",
                            marginTop: 10,
                          }}
                        >
                          <button
                            className="ns-button ns-button-outline"
                            type="button"
                            disabled={
                              authorPhotoSubmitting
                            }
                            onClick={
                              cancelAuthorPhotoRemoval
                            }
                          >
                            {isFR
                              ? "Annuler"
                              : "Cancel"}
                          </button>

                          <button
                            className="ns-button ns-button-primary"
                            type="button"
                            disabled={
                              authorPhotoSubmitting
                            }
                            onClick={
                              confirmAuthorPhotoRemoval
                            }
                          >
                            {isFR
                              ? "Confirmer le retrait"
                              : "Confirm Removal"}
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                ) : null}

                <input
                  type="file"
                  accept="image/jpeg,image/png,image/webp"
                  disabled={
                    !assetManagementEnabled ||
                    authorPhotoSubmitting
                  }
                  onChange={
                    selectAuthorPhotoFile
                  }
                />

                <p
                  className="ns-card-meta"
                  style={{
                    margin: "7px 0 0",
                  }}
                >
                  {isFR
                    ? "JPG, PNG ou WEBP. Taille maximale : 5 Mo."
                    : "JPG, PNG, or WEBP. Maximum size: 5 MB."}
                </p>

                {selectedAuthorPhotoFile ? (
                  <p
                    style={{
                      margin: "8px 0 0",
                      fontWeight: 700,
                      color: "#166534",
                    }}
                  >
                    {
                      selectedAuthorPhotoFile
                        .name
                    }
                  </p>
                ) : null}

                <label
                  style={{
                    display: "flex",
                    alignItems:
                      "flex-start",
                    gap: 9,
                    marginTop: 12,
                    lineHeight: 1.5,
                  }}
                >
                  <input
                    type="checkbox"
                    checked={
                      authorPhotoRightsConfirmed
                    }
                    onChange={(event) =>
                      setAuthorPhotoRightsConfirmed(
                        event.target.checked
                      )
                    }
                  />

                  <span>
                    {isFR
                      ? "Je confirme que je possède cette photo ou que j’ai l’autorisation de l’utiliser."
                      : "I confirm that I own this author photo or have permission to use it."}
                  </span>
                </label>

                {authorPhotoError ? (
                  <p
                    role="alert"
                    style={{
                      margin:
                        "10px 0 0",
                      color: "#b91c1c",
                      fontWeight: 700,
                    }}
                  >
                    {authorPhotoError}
                  </p>
                ) : null}

                {authorPhotoMessage ? (
                  <p
                    role="status"
                    style={{
                      margin:
                        "10px 0 0",
                      color: "#166534",
                      fontWeight: 700,
                    }}
                  >
                    {authorPhotoMessage}
                  </p>
                ) : null}

                <button
                  className="ns-button ns-button-primary"
                  type="button"
                  disabled={
                    !assetManagementEnabled ||
                    authorPhotoSubmitting ||
                    !selectedAuthorPhotoFile
                  }
                  onClick={
                    uploadSelectedAuthorPhoto
                  }
                  style={{
                    marginTop: 14,
                  }}
                >
                  {authorPhotoSubmitting
                    ? (
                        isFR
                          ? "Enregistrement…"
                          : "Saving…"
                      )
                    : activeAuthorPhoto
                      ? (
                          isFR
                            ? "Téléverser et remplacer la photo"
                            : "Upload and Replace Author Photo"
                        )
                      : (
                          isFR
                            ? "Téléverser la photo d’auteur"
                            : "Upload Author Photo"
                        )}
                </button>
              </div>

              <label
                style={{
                  display: "grid",
                  gap: 6,
                }}
              >
                <span
                  style={{
                    fontWeight: 800,
                  }}
                >
                  {isFR
                    ? "Titre sur la quatrième de couverture"
                    : "Back-cover title"}
                </span>

                <select
                  value={
                    coverPlan
                      .backCoverTitleMode ||
                    "auto"
                  }
                  onChange={(event) =>
                    update(
                      "backCoverTitleMode",
                      event.target.value
                    )
                  }
                  style={{
                    width: "100%",
                    minHeight: 42,
                    padding: "8px 10px",
                    borderRadius: 8,
                    border:
                      "1px solid #cbd5e1",
                    background: "#ffffff",
                  }}
                >
                  <option value="auto">
                    {isFR
                      ? "Automatique — laisser NextScenes décider"
                      : "Automatic — let NextScenes decide"}
                  </option>

                  <option value="show">
                    {isFR
                      ? "Afficher le titre"
                      : "Show title"}
                  </option>

                  <option value="hide">
                    {isFR
                      ? "Masquer le titre"
                      : "Hide title"}
                  </option>
                </select>

                <span
                  className="ns-card-meta"
                  style={{
                    lineHeight: 1.45,
                  }}
                >
                  {isFR
                    ? "Le mode automatique permet à NextScenes d’adapter ultérieurement la composition au livre. Ce choix ne modifie pas l’image de couverture."
                    : "Automatic allows NextScenes to adapt the composition to the book. This choice does not change the cover image."}
                </span>
              </label>

              <BackCoverTypographyControls
                isFR={isFR}
                heading={
                  isFR
                    ? "Mise en forme du titre arrière"
                    : "Back-cover title formatting"
                }
                prefix="backCoverTitle"
                coverPlan={coverPlan}
                update={update}
                minimumSize={10}
                maximumSize={36}
              />

              <Field
                multiline
                label={
                  isFR
                    ? "Texte de quatrième de couverture"
                    : "Back-cover description or blurb"
                }
                value={coverPlan.backCoverBlurb}
                onChange={(value) =>
                  update(
                    "backCoverBlurb",
                    value
                  )
                }
              />

              <BackCoverTypographyControls
                isFR={isFR}
                heading={
                  isFR
                    ? "Mise en forme du texte arrière"
                    : "Back-cover blurb formatting"
                }
                prefix="backCoverBlurb"
                coverPlan={coverPlan}
                update={update}
                allowJustify
                minimumSize={8}
                maximumSize={24}
              />

              <label
                style={{
                  display: "grid",
                  gap: 6,
                }}
              >
                <span
                  style={{
                    fontWeight: 800,
                  }}
                >
                  {isFR
                    ? "Titre sur le dos"
                    : "Spine title"}
                </span>

                <select
                  value={
                    coverPlan.spineTitleMode ||
                    "auto"
                  }
                  onChange={(event) =>
                    update(
                      "spineTitleMode",
                      event.target.value
                    )
                  }
                  style={{
                    width: "100%",
                    minHeight: 42,
                    padding: "8px 10px",
                    borderRadius: 8,
                    border:
                      "1px solid #cbd5e1",
                    background: "#ffffff",
                  }}
                >
                  <option value="auto">
                    {isFR
                      ? "Automatique — laisser NextScenes décider"
                      : "Automatic — let NextScenes decide"}
                  </option>

                  <option value="show">
                    {isFR
                      ? "Afficher le titre du livre"
                      : "Show book title"}
                  </option>

                  <option value="hide">
                    {isFR
                      ? "Masquer le titre du livre"
                      : "Hide book title"}
                  </option>
                </select>
              </label>

              <label
                style={{
                  display: "grid",
                  gap: 6,
                }}
              >
                <span
                  style={{
                    fontWeight: 800,
                  }}
                >
                  {isFR
                    ? "Auteur sur le dos"
                    : "Spine author"}
                </span>

                <select
                  value={
                    coverPlan.spineAuthorMode ||
                    "auto"
                  }
                  onChange={(event) =>
                    update(
                      "spineAuthorMode",
                      event.target.value
                    )
                  }
                  style={{
                    width: "100%",
                    minHeight: 42,
                    padding: "8px 10px",
                    borderRadius: 8,
                    border:
                      "1px solid #cbd5e1",
                    background: "#ffffff",
                  }}
                >
                  <option value="auto">
                    {isFR
                      ? "Automatique — laisser NextScenes décider"
                      : "Automatic — let NextScenes decide"}
                  </option>

                  <option value="show">
                    {isFR
                      ? "Afficher le nom de l’auteur"
                      : "Show author name"}
                  </option>

                  <option value="hide">
                    {isFR
                      ? "Masquer le nom de l’auteur"
                      : "Hide author name"}
                  </option>
                </select>
              </label>

              <p
                style={{
                  margin: "-2px 0 2px",
                  fontSize: 12,
                  lineHeight: 1.45,
                  color: "#64748b",
                }}
              >
                {isFR
                  ? "Ces choix préparent la composition automatique du dos. Le texte du dos existant reste inchangé pour le moment."
                  : "These choices prepare automatic spine composition. Existing Spine text remains unchanged for now."}
              </p>

              <Field
                label={
                  isFR
                    ? "Texte du dos"
                    : "Spine text"
                }
                value={coverPlan.spineText}
                onChange={(value) =>
                  update("spineText", value)
                }
              />
            </>
          )}

          <Field
            multiline
            label={
              isFR
                ? "Note sur les droits et le remplacement"
                : "Rights and replacement note"
            }
            value={coverPlan.rightsNote}
            onChange={(value) =>
              update("rightsNote", value)
            }
          />
        </div>
      </div>

      <div
        className="ns-card"
        style={{
          padding: 16,
          marginTop: 18,
          borderRadius: 16,
          background: "#fff7ed",
          border:
            "1px solid rgba(234,88,12,0.25)",
        }}
      >
        <strong>
          {assetManagementEnabled
            ? (
                isFR
                  ? "Espace de couverture persistant"
                  : "Persistent cover workspace"
              )
            : (
                isFR
                  ? "Plan local jusqu’au premier enregistrement"
                  : "Local plan until the first save"
              )}
        </strong>

        <p
          className="ns-card-meta"
          style={{
            margin: "7px 0 0",
            lineHeight: 1.55,
          }}
        >
          {assetManagementEnabled
            ? (
                isFR
                  ? "Utilisez Enregistrer le Blueprint pour conserver le plan textuel. La couverture sélectionnée est conservée séparément comme ressource du projet."
                  : "Use Save Blueprint to preserve the textual plan. The selected cover is stored separately as a project asset."
              )
            : (
                isFR
                  ? "Enregistrez d’abord le projet pour activer la sélection et le téléversement d’une couverture."
                  : "Save the project first to enable cover selection and upload."
              )}
        </p>
      </div>

      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          gap: 12,
          flexWrap: "wrap",
          marginTop: 18,
        }}
      >
        <button
          className="ns-button ns-button-outline"
          type="button"
          onClick={onReturn}
        >
          {returnToWholeBookReview
            ? (
                isFR
                  ? "Retour à la révision complète du livre"
                  : "Return to Whole Book Review"
              )
            : (
                isFR
                  ? "Retour au NextScenes Book Blueprint"
                  : "Return to NextScenes Book Blueprint"
              )}
        </button>

        <button
          className="ns-button ns-button-primary"
          type="button"
          onClick={() =>
            setReviewingCover(true)
          }
        >
          {isFR
            ? "Vérifier le plan de couverture"
            : "Review Cover Plan"}
        </button>
      </div>
    </section>
  );
}
